import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Clock, Plus, Pencil, Trash2, Check, ChevronDown, Search, AlertCircle, AlertTriangle, ChevronsUpDown, ChevronUp, Tag } from 'lucide-react';
import { API_URL } from '../config';
import Navbar from './Navbar';

const CustomFilterSelect = ({ label, value, onChange, options, allLabel = "ทั้งหมด", icon: Icon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(opt => String(opt.value) === String(value));

    return (
        <div className="w-full md:w-auto min-w-[140px] relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full bg-white border rounded-lg shadow-sm flex items-center px-2.5 py-1.5 gap-2 text-left ${isOpen ? '' : ''
                    }`}
            >
                {Icon && (
                    <div className={`p-1 rounded-lg flex-shrink-0 transition-colors ${isOpen ? 'bg-blue-100' : 'bg-blue-50'}`}>
                        <Icon className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                )}
                <div className="flex flex-col w-full min-w-0">
                    <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider leading-none mb-1">{label}</span>
                    <span className="text-xs font-bold text-slate-800">
                        {selectedOption?.label || allLabel}
                    </span>
                </div>
                <div className={`text-slate-300 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-500' : ''}`}>
                    <ChevronDown className="h-3 w-3" />
                </div>
            </button>

            {isOpen && (
                <div className="absolute z-[60] mt-1.5 min-w-full w-max max-w-[240px] left-0 bg-white border border-slate-100 rounded-[1.5rem] shadow-2xl py-1.5 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top">
                    <button
                        onClick={() => { onChange('All'); setIsOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-[11px] font-bold flex items-center justify-between transition-colors ${(!value || value === 'All') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <span>{allLabel}</span>
                        {(!value || value === 'All') && <Check className="w-3.5 h-3.5" />}
                    </button>
                    <div className="h-px bg-slate-100 my-1" />
                    <div className="max-h-[200px] overflow-y-auto scrollbar-hide">
                        {options.map((opt, idx) => (
                            <button
                                key={idx}
                                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                                className={`w-full text-left px-4 py-2 text-[11px] font-bold flex items-center justify-between transition-colors ${String(value) === String(opt.value) ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                            >
                                <span className="truncate">{opt.label}</span>
                                {String(value) === String(opt.value) && <Check className="w-3.5 h-3.5" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const RoundManagement = ({ token, role, username, setToken, permissions = [] }) => {
    const [rounds, setRounds] = useState([]);
    const [newRound, setNewRound] = useState({ scheduled_time: '', is_active: true });
    const [editingRound, setEditingRound] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [isRowsDropdownOpen, setIsRowsDropdownOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [filterStatus, setFilterStatus] = useState('All');
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: 'scheduled_time', direction: 'asc' });


    const fetchRounds = React.useCallback(async () => {
        try {
            // Fetch ALL rounds (including inactive)
            const res = await axios.get(`${API_URL}/api/rounds`, {
                params: { active: 'all' },
                headers: { Authorization: `Bearer ${token}` }
            });
            setRounds(res.data);
        } catch (err) {
            console.error(err);
        }
    }, [token]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchRounds();
        }, 50);
        return () => clearTimeout(timer);
    }, [fetchRounds]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filterStatus, searchTerm, rowsPerPage, sortConfig]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <ChevronsUpDown className="w-2.5 h-2.5 text-slate-300" />;
        return sortConfig.direction === 'asc' ?
            <ChevronUp className="w-2.5 h-2.5 text-blue-500" /> :
            <ChevronDown className="w-2.5 h-2.5 text-blue-500" />;
    };

    const filteredRounds = rounds.filter(r => {
        // 1. Search Time
        if (searchTerm && !r.scheduled_time.toLowerCase().includes(searchTerm.toLowerCase())) return false;
        // 2. Filter Status
        if (filterStatus !== 'All') {
            const isActive = r.is_active === 1 || r.is_active === true;
            if (filterStatus === 'Active' && !isActive) return false;
            if (filterStatus === 'Inactive' && isActive) return false;
        }
        return true;
    });


    const handleSave = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!newRound.scheduled_time) {
            setError('กรุณาระบุเวลาตรวจ');
            return;
        }

        try {
            if (editingRound) {
                await axios.put(`${API_URL}/api/rounds/${editingRound.id}`, newRound, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage(`บันทึกข้อมูลรอบ ${newRound.scheduled_time} เรียบร้อยแล้ว`);
                setShowSuccessPopup(true);
            } else {
                await axios.post(`${API_URL}/api/rounds`, newRound, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage(`เพิ่มรอบตรวจ ${newRound.scheduled_time} เรียบร้อยแล้ว`);
                setShowSuccessPopup(true);
            }

            setNewRound({ scheduled_time: '', is_active: true });
            setEditingRound(null);
            fetchRounds();
            setIsModalOpen(false);
        } catch (err) {
            if (err.response?.status === 401) return;
            console.error(err);
            // Show message from server (e.g. duplicate time)
            let msg = err.response?.data?.message || err.response?.data || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล';
            if (msg === 'Scheduled Time is required') msg = 'กรุณาระบุเวลาตรวจ';
            if (msg.includes('Duplicate entry') || msg.includes('already exists')) msg = 'มีเวลาตรวจนี้อยู่ในระบบแล้ว';

            setError(msg);
        }
    };

    const handleDelete = (id) => {
        setDeleteTargetId(id);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        setShowDeleteConfirm(false);
        try {
            await axios.delete(`${API_URL}/api/rounds/${deleteTargetId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchRounds();
            setSuccessMessage('ลบรอบตรวจเรียบร้อยแล้ว');
            setShowSuccessPopup(true);
        } catch (err) {
            if (err.response?.status === 401) return;
            console.error(err);
            const msg = err.response?.data?.message || err.response?.data || 'เกิดข้อผิดพลาดในการลบข้อมูล';
            setError(msg);
        }
        setDeleteTargetId(null);
    };

    const openModal = (round = null) => {
        setIsModalOpen(true);
        setError('');
        setSuccess('');
        if (round) {
            setEditingRound(round);
            setNewRound({
                scheduled_time: round.scheduled_time ? round.scheduled_time.slice(0, 5) : '',
                is_active: round.is_active === 1 || round.is_active === true
            });
        } else {
            setEditingRound(null);
            setNewRound({ scheduled_time: '', is_active: true });
        }
    };


    const sortedRounds = [...filteredRounds].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const totalItems = sortedRounds.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const currentRounds = sortedRounds.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const menuPerm = permissions.find(p => p.role.toLowerCase() === role.toLowerCase() && p.menu_key === 'rounds');
    const canEdit = menuPerm ? (menuPerm.can_edit === 1 || menuPerm.can_edit === true) : false;

    return (
        <div className="bg-slate-50 min-h-screen pb-10">
            <Navbar role={role} username={username} onLogout={() => setToken(null)} permissions={permissions} />

            <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-6">

                {/* Filters Bar */}
                <div className="relative md:sticky md:top-[64px] z-40 bg-slate-50/80 pt-1.5 pb-2 mb-2 -mx-2 px-2 md:-mx-4 md:px-4 transition-all">
                    <div className="bg-white p-3 sm:p-4 rounded-xl shadow-md border border-slate-200">
                        <div className="flex flex-wrap gap-2 sm:gap-4 items-center">
                            {/* Search Time */}
                            <div className="relative w-full md:w-64">
                                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                    <Search className="w-4 h-4 text-slate-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="ค้นหาเวลา (เช่น 09:00)..."
                                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 outline-none text-sm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* Filter Status */}
                            <div className="w-full md:w-auto">
                                <CustomFilterSelect
                                    label="สถานะ"
                                    icon={Tag}
                                    allLabel="ทั้งหมด"
                                    value={filterStatus}
                                    onChange={setFilterStatus}
                                    options={[
                                        { label: 'Active', value: 'Active' },
                                        { label: 'Inactive', value: 'Inactive' }
                                    ]}
                                />
                            </div>

                            <div className="ml-auto">
                                {canEdit && (
                                    <button
                                        onClick={() => openModal(null)}
                                        className="w-full md:w-auto flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-md shadow-blue-500/10 transition-all active:scale-95 border border-blue-500/20"
                                    >
                                        <Plus className="w-4 h-4 shrink-0" />
                                        <span className="truncate">เพิ่มรอบตรวจ</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error Message - Removed in favor of In-modal error or Popup */}

                {/* Success Message - Removed in favor of Popup */}
                {success && !showSuccessPopup && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 text-green-600 animate-in slide-in-from-top-2 fade-in">
                        <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        <span className="text-sm font-bold">{success}</span>
                        <button onClick={() => setSuccess('')} className="ml-auto text-green-400 hover:text-green-600 transition-colors">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                )}

                {/* Rounds List */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                        <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                            <Clock className="w-5 h-5 text-indigo-600" />
                            Round Management
                        </h2>
                        <span className="text-[9px] font-bold text-slate-500 bg-slate-200 px-2 py-1 rounded-full">{totalItems} รอบ</span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse table-fixed min-w-[700px]">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th onClick={() => requestSort('scheduled_time')} className="w-[150px] px-2 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center justify-center gap-1">เวลาตรวจ {getSortIcon('scheduled_time')}</div>
                                    </th>
                                    <th className="w-auto"></th>
                                    <th onClick={() => requestSort('created_at')} className="w-[120px] px-2 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center gap-1">วันที่สร้าง {getSortIcon('created_at')}</div>
                                    </th>
                                    <th onClick={() => requestSort('updated_at')} className="w-[120px] px-2 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center gap-1">วันที่อัปเดต {getSortIcon('updated_at')}</div>
                                    </th>
                                    <th onClick={() => requestSort('is_active')} className="w-[90px] px-2 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                        <div className="flex items-center justify-center gap-1">สถานะ {getSortIcon('is_active')}</div>
                                    </th>
                                    <th className="w-[70px] px-2 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-right"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {currentRounds.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-slate-500">
                                            <div className="flex flex-col items-center justify-center gap-2">
                                                <Search className="w-10 h-10 text-slate-300" />
                                                <p className="font-medium text-sm">ไม่พบรอบตรวจ</p>
                                                <p className="text-xs text-slate-400">ลองเปลี่ยนเงื่อนไขการค้นหาใหม่</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    currentRounds.map((r) => (
                                        <tr key={r.id} className="hover:bg-slate-50 group">
                                            <td className="px-2 py-2 align-middle text-center">
                                                <span className="text-[11px] font-black text-slate-700">
                                                    {(r.scheduled_time || '00:00:00').substring(0, 5)} น.
                                                </span>
                                            </td>
                                            <td className="w-auto"></td>
                                            <td className="px-2 py-2 align-middle text-left">
                                                <div className="flex flex-col leading-none items-start">
                                                    <span className="text-[10px] font-black text-slate-700">
                                                        {r.created_at ? new Date(r.created_at).toLocaleString('th-TH', { day: 'numeric', month: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '') : '-'}
                                                    </span>
                                                    <span className="text-[8px] text-slate-400 mt-0.5 whitespace-nowrap">by {r.creator_name || '-'}</span>
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 align-middle text-left">
                                                <div className="flex flex-col leading-none items-start">
                                                    <span className="text-[10px] font-black text-slate-700">
                                                        {r.updated_at ? new Date(r.updated_at).toLocaleString('th-TH', { day: 'numeric', month: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '') : '-'}
                                                    </span>
                                                    <span className="text-[8px] text-slate-400 mt-0.5 whitespace-nowrap">by {r.updater_name || '-'}</span>
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 align-middle text-center">
                                                <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold tracking-wide border ${(r.is_active === 1 || r.is_active === true)
                                                    ? 'bg-green-50 text-green-700 border-green-200'
                                                    : 'bg-red-50 text-red-700 border-red-200'
                                                    }`}>
                                                    {(r.is_active === 1 || r.is_active === true) ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-2 py-2 align-middle text-right">
                                                <div className="flex justify-end items-center gap-1.5">
                                                    {canEdit && (
                                                        <>
                                                            <button
                                                                onClick={() => openModal(r)}
                                                                className="text-slate-300 hover:text-blue-500 transition-colors active:scale-95"
                                                                title="แก้ไข"
                                                            >
                                                                <Pencil size={13} strokeWidth={2.5} />
                                                            </button>
                                                            <div className="w-[1px] h-2.5 bg-slate-100" />
                                                            <button
                                                                onClick={() => handleDelete(r.id)}
                                                                className="text-slate-300 hover:text-rose-500 transition-colors active:scale-95"
                                                                title="ลบ"
                                                            >
                                                                <Trash2 size={13} strokeWidth={2.5} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalItems > 0 && (
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">แสดงรายการ:</span>
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setIsRowsDropdownOpen(!isRowsDropdownOpen)}
                                        className="bg-white border border-slate-200 rounded-lg px-2 py-1 min-w-[55px] flex items-center justify-between gap-1.5 shadow-sm hover:border-slate-300 hover:bg-slate-50 outline-none focus:ring-2 focus:ring-blue-50"
                                    >
                                        <span className="text-[12px] font-bold text-slate-700">{rowsPerPage}</span>
                                        <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${isRowsDropdownOpen ? 'rotate-180 text-blue-500' : ''}`} />
                                    </button>

                                    {isRowsDropdownOpen && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={() => setIsRowsDropdownOpen(false)} />
                                            <div className="absolute bottom-full left-0 mb-2 min-w-full w-max bg-white border border-slate-100 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                <div className="py-0.5">
                                                    {[10, 20, 50, 100].map(size => (
                                                        <button
                                                            key={size}
                                                            onClick={() => {
                                                                setRowsPerPage(size);
                                                                setIsRowsDropdownOpen(false);
                                                                setCurrentPage(1);
                                                            }}
                                                            className={`w-full px-3 py-1.5 text-[12px] font-bold flex items-center justify-between transition-colors ${rowsPerPage === size ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                        >
                                                            <span>{size}</span>
                                                            {rowsPerPage === size && <Check className="w-3 h-3" />}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider ml-2">
                                    {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, totalItems)} จาก {totalItems}
                                </span>
                            </div>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className={`p-1.5 rounded-lg transition-all ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="First Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className={`p-1.5 rounded-lg transition-all ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Previous Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                                </button>

                                <div className="flex items-center gap-1 px-2">
                                    {/* Show limited page numbers */}
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) pageNum = i + 1;
                                        else if (currentPage <= 3) pageNum = i + 1;
                                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                        else pageNum = currentPage - 2 + i;

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-black transition-all ${currentPage === pageNum ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className={`p-1.5 rounded-lg transition-all ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Next Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                                </button>
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className={`p-1.5 rounded-lg transition-all ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Last Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setIsModalOpen(false)}
                    />
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-hidden flex flex-col z-10 animate-in zoom-in-95 fade-in duration-200 border border-slate-200">
                        {/* Header */}
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                            <div>
                                <h2 className="text-sm font-bold text-slate-800 tracking-tight">
                                    {editingRound ? 'แก้ไขรอบตรวจ' : 'เพิ่มรอบตรวจใหม่'}
                                </h2>
                                <p className="text-xs font-medium text-slate-400 mt-0.5">ระบุเวลาที่ต้องการให้เจ้าหน้าที่ตรวจสอบ</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all font-bold"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="flex flex-col flex-1 overflow-hidden">
                            <div className="p-4 space-y-4 overflow-y-auto flex-1">


                                {/* Section 1: Round Info */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-1 h-3 bg-blue-500 rounded-full"></div>
                                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">ข้อมูลรอบตรวจ</h3>
                                    </div>

                                    {/* <div className={`px-3 py-1.5 border rounded-lg flex items-start gap-2 bg-amber-50/50 border-amber-100`}>
                                        <AlertCircle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500`} />
                                        <p className={`text-[10px] font-medium leading-relaxed text-amber-700`}>
                                            ระบุเวลาตรวจที่เจ้าหน้าที่ต้องลงพื้นที่ตรวจสอบ โดยคุณสามารถกำหนดเวลาได้ทุกๆ 15 นาที
                                        </p>
                                    </div> */}

                                    <div className="space-y-2">
                                        {/* <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">เวลาตรวจ</label>
                                         */}
                                        {/* Ultra-Minimalist Compact Time Picker */}
                                        <div className="bg-slate-50/50 border border-slate-200 rounded-xl p-3 space-y-4">
                                            {/* Hours Grid - Tiny circles */}
                                            <div className="space-y-1.5">
                                                <span className="text-[8px] font-black text-slate-400 tracking-widest">Hour</span>
                                                <div className="grid grid-cols-8 gap-1">
                                                    {Array.from({ length: 24 }).map((_, i) => {
                                                        const h = i.toString().padStart(2, '0');
                                                        const isSelected = newRound.scheduled_time?.split(':')[0] === h;
                                                        return (
                                                            <button
                                                                key={h}
                                                                type="button"
                                                                onClick={() => {
                                                                    const m = newRound.scheduled_time?.split(':')[1] || '00';
                                                                    setNewRound({ ...newRound, scheduled_time: `${h}:${m}` });
                                                                }}
                                                                className={`text-[9px] font-bold h-7 rounded-lg transition-all ${isSelected
                                                                    ? 'bg-blue-600 text-white shadow-sm'
                                                                    : 'bg-white text-slate-500 hover:bg-slate-100 border border-slate-100'
                                                                    }`}
                                                            >
                                                                {h}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Minutes Grid - Tiny pills */}
                                            <div className="space-y-1.5">
                                                <span className="text-[8px] font-black text-slate-400 tracking-widest">Minute</span>
                                                <div className="grid grid-cols-4 gap-1.5">
                                                    {['00', '15', '30', '45'].map((m) => {
                                                        const isSelected = newRound.scheduled_time?.split(':')[1] === m;
                                                        return (
                                                            <button
                                                                key={m}
                                                                type="button"
                                                                onClick={() => {
                                                                    const h = newRound.scheduled_time?.split(':')[0] || '09';
                                                                    setNewRound({ ...newRound, scheduled_time: `${h}:${m}` });
                                                                }}
                                                                className={`py-1 text-[9px] font-bold rounded-lg transition-all ${isSelected
                                                                    ? 'bg-blue-600 text-white shadow-sm'
                                                                    : 'bg-white text-slate-500 hover:bg-slate-100 border border-slate-100'
                                                                    }`}
                                                            >
                                                                {m}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Summary Display - Small & Clean */}
                                            <div className="pt-2 border-t border-slate-100 flex items-center justify-center gap-2">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">เลือกเวลา:</span>
                                                <span className="text-sm font-black text-blue-600 tracking-wider bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">
                                                    {newRound.scheduled_time || '--:--'} น.
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">สถานะ</label>
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                                                    onBlur={() => setTimeout(() => setIsStatusDropdownOpen(false), 200)}
                                                    className="w-full bg-white border border-slate-200 rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-2 text-left group outline-none"
                                                >
                                                    <span className={`text-[13px] font-medium truncate flex-1 ${(newRound.is_active === 1 || newRound.is_active === true) ? 'text-blue-600' : 'text-slate-500'}`}>
                                                        {(newRound.is_active === 1 || newRound.is_active === true) ? 'Active' : 'Inactive'}
                                                    </span>
                                                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isStatusDropdownOpen ? 'rotate-180 text-blue-500' : 'text-slate-400'}`} />
                                                </button>
                                                {isStatusDropdownOpen && (
                                                    <div className="absolute bottom-full mb-2 w-full bg-white border border-slate-100 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                        <div className="py-1">
                                                            {[
                                                                { label: 'Active', value: true },
                                                                { label: 'Inactive', value: false }
                                                            ].map(opt => (
                                                                <button
                                                                    key={opt.label}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setNewRound({ ...newRound, is_active: opt.value });
                                                                        setIsStatusDropdownOpen(false);
                                                                    }}
                                                                    className={`w-full text-left px-4 py-2 text-[13px] font-bold flex items-center justify-between transition-colors ${(newRound.is_active === 1 || newRound.is_active === true) === opt.value ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                >
                                                                    <span>{opt.label}</span>
                                                                    {(newRound.is_active === 1 || newRound.is_active === true) === opt.value && <Check className="w-4 h-4 text-blue-600" />}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="mx-5 mb-4 p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-3 text-rose-600 animate-in slide-in-from-bottom-2 duration-200">
                                    <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                                        <AlertTriangle size={18} strokeWidth={2.5} />
                                    </div>
                                    <span className="text-xs leading-tight font-medium">{error}</span>
                                </div>
                            )}

                            {/* Footer */}
                            <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex gap-3 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-2.5 rounded-xl text-slate-600 text-sm font-bold hover:bg-slate-100 border border-slate-200 active:scale-95 transition-all"
                                >
                                    ยกเลิก
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-500/25 active:scale-95 transition-all"
                                >
                                    {editingRound ? 'บันทึกแก้ไข' : 'บันทึก'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Popup */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowDeleteConfirm(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[340px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6">
                                <Trash2 className="w-8 h-8 text-rose-500" />
                            </div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">ยืนยันการลบข้อมูล?</h3>
                            <p className="text-[13px] text-slate-400 mt-2">ข้อมูลนี้จะถูกลบถาวร ไม่สามารถกู้คืนได้</p>

                            <div className="flex gap-3 w-full mt-10">
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="flex-1 py-2.5 border border-slate-100 text-slate-500 rounded-2xl font-black text-[14px]  active:scale-95 hover:bg-slate-50 shadow-sm"
                                >
                                    ยกเลิก
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="flex-1 py-2.5 bg-rose-500 text-white rounded-2xl font-black text-[14px] shadow-lg shadow-rose-200  active:scale-95 hover:bg-rose-600  tracking-wide"
                                >
                                    ลบข้อมูล
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Success Popup Modal */}
            {showSuccessPopup && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowSuccessPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center">
                            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                                <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">ทำรายการสำเร็จ</h3>
                            <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed">{successMessage}</p>

                            <button
                                onClick={() => setShowSuccessPopup(false)}
                                className="mt-8 w-full py-2.5 bg-blue-600 text-white rounded-2xl font-black text-[14px] tracking-wide  active:scale-95 hover:bg-blue-700 shadow-lg shadow-blue-500/20"
                            >
                                ตกลง
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Popup Modal */}
            {showErrorPopup && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowErrorPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                                <AlertCircle className="w-8 h-8 text-rose-500" />
                            </div>
                            <h3 className="text-xl font-black text-rose-600 tracking-tight">เกิดข้อผิดพลาด</h3>
                            <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed">{error}</p>

                            <button
                                onClick={() => setShowErrorPopup(false)}
                                className="mt-8 w-full py-2.5 bg-slate-800 text-white rounded-2xl font-black text-[14px] tracking-wide  active:scale-95 hover:bg-slate-900 shadow-lg shadow-slate-200"
                            >
                                เข้าใจแล้ว
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoundManagement;
